import React, { Component } from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { withTheme } from 'styled-components';

import { Heading } from '@movember/mo-gel';
import { StyledMenLikeMePage, StyledHeadingContainer } from './MenLikeMe.styled';
import { InputGroup } from '../../containers/InputGroup';
import { OutcomesGroup } from '../../containers/OutcomesGroup';
import { LowLevelGroup } from '../../containers/LowLevelGroup';
import { getFeatureFlags } from '../../services/featureFlagService';
import { generateUniqueNumber } from '../../utils/numbers';

import { pushFormStart, pushRouteChange } from '../../utils/gtmEventPush';

import { areEqual } from '../../utils/equalityChecker';

class MenLikeMe extends Component {
  componentName = 'MenLikeMe.page';

  constructor(props) {
    super(props);

    this.state = {
      selectedInputGroup: [],
      selectedSubInputs: [],
      outcomeIndex: 'NNNN',
      filteredOutcomes: [],
      showOutcomes: false,
      categoryDomain: '',
      showLowLevel: false,

      // Google Tag Manager Data Layer
      processId: generateUniqueNumber(),
      formStartTime: new Date().getTime(),
      selectedValues: '', // eslint-disable-line react/no-unused-state
      routeInfo: {
        currentRoute: 'treatment',
        previousRoute: '',
        title: 'treatment'
      },
      skipInputLoad: false
    };
  }

  componentDidMount() {
    const { onRequestInputs, onRequestOutcomes } = this.props;
    const { formStartTime, processId } = this.state;

    onRequestInputs();
    onRequestOutcomes();

    pushFormStart((new Date().getTime() - formStartTime) / 1000, processId);

    this.setStateFromHistory();
    window.addEventListener('popstate', this.onBackOrForwardButtonEvent);
  }

  componentDidUpdate() {
    if (!window.history.state || !areEqual(window.history.state[this.componentName], this.state)) {
      const newState = {};
      Object.assign(newState, window.history.state);
      newState[this.componentName] = this.state;
      if (window.history.state && Object.prototype.hasOwnProperty.call(window.history.state, this.componentName)) {
        window.history.pushState(newState, '');
      } else {
        window.history.replaceState(newState, '');
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.onBackOrForwardButtonEvent);
  }

  onBackOrForwardButtonEvent = () => {
    this.setStateFromHistory();
  };

  setStateFromHistory = () => {
    if (window.history.state && Object.prototype.hasOwnProperty.call(window.history.state, this.componentName)) {
      if (!areEqual(window.history.state[this.componentName], this.state)) {
        this.setState(window.history.state[this.componentName], () => {
          this.setThemeFromProps();
        });
      }
    }
  };

  setThemeFromProps = () => {
    const { showOutcomes, showLowLevel } = this.state;
    const { onThemeChanged, theme } = this.props;
    theme.dark = !showOutcomes && !showLowLevel;
    onThemeChanged(theme);
  };

  getOutcomeResults(inputGroupIndex) {
    const { outcomes, fetchingOutcomes } = this.props;
    const loadingOutcomes = !outcomes || fetchingOutcomes;
    if (!loadingOutcomes) {
      const results = outcomes.find((outcome) => outcome.index === inputGroupIndex);
      if (results && results.outcomes) this.setState({ filteredOutcomes: results.outcomes });
    }
  }

  onInputCompleted = (selectedInputGroup, outcomeIndex, selectedSubInputs) => {
    const { onThemeChanged, theme } = this.props;
    theme.dark = false;
    this.getOutcomeResults(outcomeIndex);
    this.setState({ outcomeIndex });
    this.setState({ showOutcomes: true, selectedInputGroup, selectedSubInputs });
    onThemeChanged(theme);
  };

  onEditInputs = () => {
    const { onThemeChanged, theme } = this.props;
    theme.dark = true;
    this.setState({ showOutcomes: false });
    onThemeChanged(theme);
    this.setState({ skipInputLoad: true });
  };

  onToggleLowLevel = (categoryLabel) => {
    const { showOutcomes, showLowLevel } = this.state;
    this.setState({ showOutcomes: !showOutcomes, showLowLevel: !showLowLevel, categoryDomain: categoryLabel });
    window.scrollTo(0, 0);
  };

  onSkipInputLoad = () => {
    this.setState({ skipInputLoad: false });
  };

  updateRoute = (data) => {
    this.setState({
      routeInfo: {
        currentRoute: data.currentRoute,
        previousRoute: data.previousRoute,
        title: data.title
      }
    });

    pushRouteChange(data.currentRoute, data.previousRoute);
  };

  render() {
    const {
      selectedInputGroup,
      filteredOutcomes,
      categoryDomain,
      showOutcomes,
      showLowLevel,
      outcomeIndex,
      selectedSubInputs,
      processId,
      formStartTime,
      routeInfo,
      skipInputLoad
    } = this.state;
    const { inputs, fetchingInputs, fetchingOutcomes, isBlurred, theme } = this.props;
    const featureFlags = getFeatureFlags();

    return (
      <StyledMenLikeMePage isBlurred={isBlurred}>
        {featureFlags.MenLikeMeEnabled ? (
          <>
            {!showOutcomes && !showLowLevel && inputs && (
              <InputGroup
                inputs={inputs}
                selectedInputGroup={selectedInputGroup}
                selectedSubInputGroup={selectedSubInputs}
                fetching={fetchingInputs || fetchingOutcomes}
                onInputCompleted={this.onInputCompleted}
                processId={processId}
                formStartTime={formStartTime}
                routeInfo={routeInfo}
                skipInputLoad={skipInputLoad}
                onSkipInputLoad={this.onSkipInputLoad}
              />
            )}
            {!showLowLevel && showOutcomes && (
              <OutcomesGroup
                outcomes={filteredOutcomes}
                outcomeIndex={outcomeIndex}
                onEdit={this.onEditInputs}
                onViewLowLevel={this.onToggleLowLevel}
                updateRoute={this.updateRoute}
              />
            )}
            {showLowLevel && (
              <LowLevelGroup
                outcomes={filteredOutcomes}
                outcomeIndex={outcomeIndex}
                treatment={selectedInputGroup[0].selectedValue.text}
                categoryDomain={categoryDomain}
                onBack={this.onToggleLowLevel}
                theme={theme}
              />
            )}
          </>
        ) : (
          <StyledHeadingContainer>
            <Heading level="h2" color="#ffffff">
              Coming soon...
            </Heading>
          </StyledHeadingContainer>
        )}
      </StyledMenLikeMePage>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    fetchingInputs: state.fetchingInputs,
    fetchingOutcomes: state.fetchingOutcomes,
    inputs: state.inputs,
    outcomes: state.outcomes
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    onRequestInputs: () => dispatch({ type: 'GET_MLM_INPUTS_REQUEST' }),
    onRequestOutcomes: () => dispatch({ type: 'GET_MLM_OUTCOMES_REQUEST' })
  };
};

MenLikeMe.defaultProps = {
  outcomes: {},
  inputs: {},
  fetchingInputs: false,
  fetchingOutcomes: false
};

MenLikeMe.propTypes = {
  inputs: PropTypes.arrayOf(PropTypes.object),
  outcomes: PropTypes.arrayOf(PropTypes.object),
  fetchingInputs: PropTypes.bool,
  fetchingOutcomes: PropTypes.bool,
  onRequestInputs: PropTypes.func.isRequired,
  onRequestOutcomes: PropTypes.func.isRequired
};

export const MenLikeMePage = withTheme(connect(mapStateToProps, mapDispatchToProps)(MenLikeMe));

// Default export is an unconnected component primarily used for unit testing
export default MenLikeMe;
